import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  postCreateComicList,
  postCreateReleaseList,
  CreateListPayload,
} from "../api/lists";
import { READING_LISTS_SHELF_QUERY_KEY } from "../screens/shelf/hooks/useReadingListsShelf";

interface UseListMutationsOptions {
  entityType: "comic" | "release";
  entityId: number;
  onCreateSuccess?: () => void;
  addCurrentEntityToNewList?: boolean;
}

// Narrows a thrown value (typically an AxiosError but typed as unknown by
// react-query's mutation surface) to a human-readable message without
// reaching for `any`. Prefers the server-supplied message when present —
// that's what we already show in our own error toasts elsewhere.
function getMutationErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null) {
    const e = err as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return e.response?.data?.message ?? e.message ?? fallback;
  }
  return fallback;
}

export function useListMutations({
  entityType,
  entityId,
  onCreateSuccess,
  addCurrentEntityToNewList = false,
}: UseListMutationsOptions) {
  const [t] = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Word for the entity type used in toast templates (i18n'd, so a future
  // translator can substitute the noun gender / form for each language).
  const entityTypeLabel = t(
    entityType === "comic"
      ? "components.lists.addToList.entityTypeComic"
      : "components.lists.addToList.entityTypeRelease",
  );

  const genericErrorFallback = t(
    "components.lists.addToList.genericErrorFallback",
  );

  // Invalidate every cached representation of "the user's lists" so any
  // mounted surface (AddToListButton on another entity page, the shelf
  // rail, etc.) sees the new state immediately. Two key conventions live
  // in the codebase: the comic/release-typed legacy keys the popover
  // reads, and READING_LISTS_SHELF_QUERY_KEY which the shelf rail + wizard
  // use. Hitting both means cross-surface state doesn't drift behind a
  // stale-time wait.
  const invalidateListCaches = () => {
    queryClient.invalidateQueries({ queryKey: [`user-${entityType}-lists`] });
    queryClient.invalidateQueries({ queryKey: READING_LISTS_SHELF_QUERY_KEY });
  };

  // Create list mutation. When `addCurrentEntityToNewList` is true we fold
  // the add into the create payload — backend's /v1/lists/create accepts
  // { entity_type, list_ids } inline and returns items_added/items_failed,
  // saving a round-trip vs the prior create-then-add sequence.
  const createListMutation = useMutation({
    mutationFn: (payload: CreateListPayload) => {
      const withInline: CreateListPayload =
        addCurrentEntityToNewList && entityId
          ? { ...payload, entity_type: entityType, list_ids: [entityId] }
          : payload;
      return entityType === "comic"
        ? postCreateComicList(withInline)
        : postCreateReleaseList(withInline);
    },
    onSuccess: (response) => {
      invalidateListCaches();

      const result = response.data.payload.results;
      const addFailed = (result.items_failed?.length ?? 0) > 0;

      if (addCurrentEntityToNewList && addFailed) {
        toast({
          title: t("components.lists.addToList.createAndAddPartialTitle"),
          description: t(
            "components.lists.addToList.createAndAddPartialDescription",
            { name: result.display_name, entityType: entityTypeLabel },
          ),
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else if (addCurrentEntityToNewList) {
        toast({
          title: t("components.lists.addToList.createAndAddSuccessTitle"),
          description: t(
            "components.lists.addToList.createAndAddSuccessDescription",
            { name: result.display_name, entityType: entityTypeLabel },
          ),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("components.lists.addToList.createSuccessTitle"),
          description: t(
            "components.lists.addToList.createSuccessDescription",
            { name: result.display_name },
          ),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onCreateSuccess?.();
    },
    onError: (error: unknown) => {
      toast({
        title: t("components.lists.addToList.createError"),
        description: getMutationErrorMessage(error, genericErrorFallback),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  return {
    createListMutation,
    isLoading: createListMutation.isPending,
  };
}
